import type { UseQueryResult } from '@tanstack/react-query';
import { SortOrder, type Certification } from '__generated__/graphql';
import classNames from 'classnames';
import DropDownPicker from 'components/foundation/pickers/DropDownPicker/DropDownPicker';
import SimplePickerRow from 'components/foundation/pickers/SimplePickerRow/SimplePickerRow';
import { useCertificationQuery } from 'hooks/models/certification/useCertificationQuery/useCertificationQuery';
import * as React from 'react';
import { ReactNode } from 'react';
import { ClassName } from 'types/ClassName';

export interface ICertificationPickerProps {
  value: string | null;
  onSelect: (certificationId: string | null) => void;
  placeholder?: string;
  error?: boolean;
  className?: ClassName;
  dropDownButtonRender?: ReactNode;
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode;
}

function useCertificationPickerOptionsQuery() {
  return useCertificationQuery({
    variables: {
      filter: { deletedOn: { isNull: true } },
      sort: [{ name: SortOrder.Asc }],
    },
  });
}

function useSelectedCertificationQuery(id: string | null) {
  const query = useCertificationQuery({
    variables: {
      filter: { id: { equal: id } },
      first: 1,
    },
    options: { enabled: !!id },
  });

  return {
    ...query,
    data: query.data ? query.data?.[0] : null,
  } as UseQueryResult<Certification | null, Error>;
}

function CertificationPicker(props: ICertificationPickerProps) {
  const { value, onSelect, error, placeholder, closeButtonRender, dropDownButtonRender, className } = props;

  const certificationsQuery = useCertificationPickerOptionsQuery();
  const selectedCertificationQuery = useSelectedCertificationQuery(value);

  function handleSelect(model: Certification | null) {
    return onSelect(model ? model.id : null);
  }

  function renderValueTemplate() {
    if (selectedCertificationQuery.data) {
      return <>{selectedCertificationQuery.data.name}</>;
    }

    return <></>;
  }

  function renderRow(certification: Certification) {
    return <SimplePickerRow value={certification.name} />;
  }

  const classes = classNames('certification-picker', className);

  return (
    <DropDownPicker
      value={value}
      error={error}
      data={certificationsQuery.data ?? []}
      loadedAll={true}
      onSelect={handleSelect}
      valueTemplate={renderValueTemplate()}
      renderRow={renderRow}
      placeholder={placeholder}
      className={classes}
      closeButtonRender={closeButtonRender}
      dropDownButtonRender={dropDownButtonRender}
      dataError={!!certificationsQuery.error || !!selectedCertificationQuery.error}
      isLoading={certificationsQuery.isLoading || selectedCertificationQuery.isLoading}
    />
  );
}

export default CertificationPicker;
